import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Minus, GraduationCap, Award, ArrowRight, CalendarCheck, Check } from "lucide-react";
import { Reveal, EASE } from "./motion";
import { PhotoSlot } from "./PhotoSlot";
import { DasProfile } from "./DasProfile";
import { DOCTORS, DR_DAS_INTERESTS } from "../lib/constants";

const DoctorCard = ({ d, index }) => {
  const [open, setOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  return (
    <Reveal delay={index * 0.08}>
      <div className="rounded-3xl border border-slate-200 bg-white overflow-hidden card-lift" data-testid={`doctor-card-${index}`}>
        <div className="grid lg:grid-cols-[0.8fr_1.2fr]">
          <div className="relative h-80 lg:h-auto lg:min-h-[420px] bg-brand-mist">
            <PhotoSlot
              label={d.name}
              note={d.degrees}
              src={d.src}
              alt={d.alt}
              className="absolute inset-0 w-full h-full"
            />
          </div>
          <div className="p-7 sm:p-10">
            <span className="kicker">{d.role}</span>
            <h3 className="mt-3 text-2xl sm:text-3xl font-extrabold tracking-tight text-ink">{d.name}</h3>
            <p className="mt-1.5 text-sm font-bold text-aqua">{d.degrees}</p>
            <div className="mt-5 flex flex-wrap gap-2">
              {d.badges.map((b, i) => (
                <span
                  key={b}
                  className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-bold text-ink ${
                    i % 2 === 0 ? "bg-brand-mist" : "bg-aqua-mist"
                  }`}
                >
                  {i % 2 === 0 ? (
                    <GraduationCap className="w-3.5 h-3.5 text-brand-bright" aria-hidden="true" />
                  ) : (
                    <Award className="w-3.5 h-3.5 text-aqua" aria-hidden="true" />
                  )}
                  {b}
                </span>
              ))}
              {d.chips.map((c) => (
                <span key={c} className="rounded-full border border-ink/12 px-3.5 py-1.5 text-xs font-bold text-ink/75">
                  {c}
                </span>
              ))}
            </div>
            {d.stats && (
              <div className="mt-6 grid grid-cols-3 divide-x divide-slate-100 rounded-2xl border border-slate-100 bg-slate-50">
                {d.stats.map(([v, l]) => (
                  <div key={l} className="px-3 py-4 text-center">
                    <p className="text-lg sm:text-2xl font-extrabold text-ink">{v}</p>
                    <p className="mt-1 text-[10px] sm:text-[11px] font-bold uppercase tracking-wider text-slate-500">{l}</p>
                  </div>
                ))}
              </div>
            )}
            <button
              className="mt-6 inline-flex items-center gap-2 text-sm font-bold text-brand-bright hover:gap-3 transition-[gap]"
              onClick={() => setOpen(!open)}
              aria-expanded={open}
              data-testid={`doctor-detail-toggle-${index}`}
            >
              {open ? <Minus className="w-4 h-4" aria-hidden="true" /> : <Plus className="w-4 h-4" aria-hidden="true" />}
              {open ? "Hide Details" : d.bioLabel}
            </button>
            <AnimatePresence initial={false}>
              {open && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.4, ease: EASE }}
                  className="overflow-hidden"
                >
                  {d.bio ? (
                    <p className="mt-4 text-sm sm:text-base leading-relaxed text-slate-600 border-l-4 border-aqua pl-4" data-testid={`doctor-bio-text-${index}`}>
                      {d.bio}
                    </p>
                  ) : (
                    <ul className="mt-4 space-y-2.5" data-testid={`doctor-quals-${index}`}>
                      {d.quals.map((q) => (
                        <li key={q} className="flex items-start gap-2.5 rounded-xl bg-slate-50 border border-slate-100 px-4 py-3 text-sm font-semibold text-ink/85">
                          <Check className="w-4 h-4 text-aqua shrink-0 mt-0.5" aria-hidden="true" />
                          {q}
                        </li>
                      ))}
                    </ul>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
            <div className="mt-7 flex flex-wrap gap-3">
              {d.profileLink && (
                <Link to={d.profileLink} className="btn-outline !text-[13px]" data-testid={`doctor-profile-button-${index}`}>
                  View Full Profile <ArrowRight className="w-4 h-4" aria-hidden="true" />
                </Link>
              )}
              {d.hasProfile && (
                <button
                  onClick={() => setProfileOpen(!profileOpen)}
                  className="btn-outline !text-[13px]"
                  aria-expanded={profileOpen}
                  data-testid={`doctor-profile-toggle-${index}`}
                >
                  {profileOpen ? "Hide Professional Profile" : "View Professional Profile"}
                </button>
              )}
              <Link to="/contact#book" className="btn-primary !text-[13px]" data-testid={`doctor-book-button-${index}`}>
                <CalendarCheck className="w-4 h-4" aria-hidden="true" /> Book Consultation
              </Link>
            </div>
          </div>
        </div>
        <AnimatePresence initial={false}>
          {d.hasProfile && profileOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.45, ease: EASE }}
              className="overflow-hidden border-t border-slate-100"
            >
              <div className="p-6 sm:p-10 sm:pt-8 bg-slate-50/60" data-testid={`doctor-profile-panel-${index}`}>
                <p className="text-sm text-slate-600 leading-relaxed max-w-3xl">
                  <strong className="text-ink">Core expertise:</strong> Orbit, Oculoplasty, Oncology and reconstructive
                  surgery — the conditions he treats and the procedures he performs.
                </p>
                <div className="mt-6">
                  <DasProfile />
                </div>
                <div className="mt-6 rounded-2xl bg-ink text-white noise-dark relative overflow-hidden p-7 sm:p-8">
                  <div className="relative">
                    <span className="kicker !text-aqua-bright">Special Interests</span>
                    <ul className="mt-4 space-y-3">
                      {DR_DAS_INTERESTS.map((s, i) => (
                        <li key={i} className="flex items-start gap-3 text-sm text-blue-100/90 leading-relaxed">
                          <span className="mt-2 w-1.5 h-1.5 rounded-full bg-aqua-bright shrink-0" aria-hidden="true" />
                          {s}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </Reveal>
  );
};

export const DoctorsSection = () => (
  <div className="space-y-8" data-testid="doctors-list">
    {DOCTORS.map((d, i) => (
      <DoctorCard key={d.name} d={d} index={i} />
    ))}
  </div>
);

